import React, { Component } from "react";
import Header from "./comp/header";
import API from "./api"; // Import the API class
import tests from "./tests";
import TestCard from "./comp/test";
import { doOauthLogin, initOauthLogin } from "./helper/oauth_login";

const api = new API(); // Default API host

class App extends Component {
  state = {
    appName: null,
    loading: true,
    error: null,
    tests: tests(api),
    testsRunning: false,
    isPrestine: true,
    showApiForm: false,
    user: null // new user state
  };

  async componentDidMount() {
    this.setState({ loading: true });

    let apiHost = this.getCache("apiHost");
    if (!apiHost) {
      apiHost = "https://advance.demo.letsflo.io/api";
      this.setCache("apiHost", apiHost);
    }
    api.setHost(apiHost);

    await this.fetchAppDetails();

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code) {
      let token = await doOauthLogin(code, api);
      if (token) {
        this.setCache("token", token);
      } else {
        this.setState({
          error: "OAuth login failed. Please try again.",
          loading: false,
          showApiForm: true
        });
        return;
      }

      // Remove code from URL
      window.history.replaceState({}, document.title, "/");
    }

    let token = this.getCache("token");
    if (token) {
      api.setToken(token);
      await this.fetchUser();
    }

    this.setState({ loading: false });
  }

  fetchAppDetails = async () => {
    let appDetails = this.getCache("app_details");
    if (!appDetails) {
      try {
        appDetails = await api.get("/app");
      } catch (error) {
        console.info("fetchAppDetails", error);
        this.clearCache("app_details");
        this.setState({
          error: error.message || "Failed to fetch app details",
          loading: false,
          showApiForm: true
        });
        retur;
      }
      this.setCache("app_details", appDetails);
    }

    this.setState({ appName: appDetails.name });

    let floOauth = appDetails.auth.oauths.find(n => n.slug == "flo-mso");
    if (floOauth) {
      this.setState({ floOauth });
    }
  };

  _startOauthLogin = async () => {
    const { floOauth } = this.state;

    return initOauthLogin(floOauth);
  };

  logout = () => {
    api.setToken("token", "");
    this.clearCache("token");
    this.clearCache("user");
  };

  fetchUser = async () => {
    let user = this.getCache("user");
    if (!user) {
      const res = await api.get("/users");
      if (res && !res.error && res.length > 0) {
        user = res[0];
        this.setCache("user", user);
      }
    }

    if (!user) {
      this.clearCache("token");
      this.setState({ showApiForm: true });
      return;
    }
  };

  handleFormSubmit = e => {
    e.preventDefault();
    this.setState({ app_details: null, appName: "" });

    let { apiHost } = this.state;

    apiHost = this.cleanupUrl(apiHost);
    if (!apiHost) {
      this.setState({ error: "Invalid API Host URL" });
      return;
    }

    this.setCache("apiHost", apiHost);
    api.setHost(apiHost);
    this.fetchAppDetails()
      .then(() => {
        let token = this.getCache("token");
        api.setToken(token);
        return this.fetchUser();
      })
      .then(() => {
        this.setState({
          isPrestine: true,
          testsRunning: false,
          error: null
        });
      })
      .catch(e => {
        this.setState({
          showApiForm: true,
          isPrestine: true,
          testsRunning: false,
          error: e.message
        });
      });
  };

  startRun() {
    this.setState({ testsRunning: true, isPrestine: false }, () =>
      this.runTestsSequentially()
    );
  }

  async runTestsSequentially() {
    for (let test of this.state.tests) {
      if (!this.state.testsRunning) return;
      if (test.status === "not started") {
        await test.run();
        this.setState({ tests: [...this.state.tests] });
      }
    }
    this.setState({ testsRunning: false });
  }

  resetAllTests() {
    for (let test of this.state.tests) {
      test.reset();
    }
    this.setState({ testsRunning: false, isPrestine: true });
  }

  stopRun() {
    this.setState({ testsRunning: false });
  }

  cleanupUrl(input) {
    if (input.includes("localhost")) {
      input = "http://localhost:3200";
    } else if (!input.startsWith("https://")) {
      const baseUrlPattern = /^https:\/\/[\w.-]+\.letsflo\.io\/api$/;
      input = `https://${input}`;
      if (!input.endsWith("letsflo.io/api")) {
        if (input.includes("letsflo.io")) {
          input = input.replace(/\/?$/, "/api");
        } else {
          input = `${input}.letsflo.io/api`;
        }
      }
      if (!baseUrlPattern.test(input)) {
        return null;
      }
    }

    return input;
  }

  setCache(key, value) {
    localStorage.setItem(key + "_auto", JSON.stringify(value));
    this.setState({ [key]: value });
  }
  getCache(key) {
    if (this.state?.[key]) {
      return this.state[key];
    }
    let value = localStorage.getItem(key + "_auto");
    if (value) {
      value = JSON.parse(value);
      this.setState({ [key]: value });
      return value;
    }
    return null;
  }
  clearCache(key) {
    localStorage.removeItem(key + "_auto");
    this.setState({ [key]: null });
  }

  render() {
    const {
      appName,
      loading,
      error,
      showApiForm,
      apiHost,
      token,
      user,
      floOauth
    } = this.state;

    return (
      <div className="min-h-screen bg-gray-100 pt-16">
        <Header appName={appName} />
        <main>
          <div className="container mx-auto p-4 mt-6">
            <div className="bg-white shadow-md rounded-lg p-6 mb-4">
              {loading && (
                <p className="text-gray-700">Loading app details...</p>
              )}
              {error && <p className="text-red-500">{error}</p>}
              {!loading && !error && (
                <div>
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800">
                        Instance: {appName}
                      </h2>
                      {user && (
                        <p className="text-sm text-gray-500 mt-1">
                          Testing as: {user.name || user.email || "Unknown"}
                        </p>
                      )}
                    </div>
                    <div className="flex">
                      {this.state.testsRunning ? (
                        <button
                          onClick={() => this.stopRun()}
                          className="ml-1 bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center hover:shadow-lg"
                        >
                          ⏸
                        </button>
                      ) : (
                        <button
                          onClick={() => this.startRun()}
                          className="ml-1 bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center hover:shadow-lg"
                        >
                          ▶
                        </button>
                      )}
                      {!this.state.isPrestine && (
                        <button
                          onClick={() => this.resetAllTests()}
                          className="ml-1 bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center hover:shadow-lg"
                        >
                          🔄
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center space-x-2">
                    <button
                      className="focus:outline-none"
                      onClick={() =>
                        this.setState({ showApiForm: !showApiForm })
                      }
                      title="Toggle API Settings"
                    >
                      <span
                        className={`inline-block transform transition-transform duration-200 text-gray-500 ${
                          showApiForm ? "rotate-90" : "rotate-0"
                        }`}
                      >
                        ▶
                      </span>
                      <span className="text-sm text-gray-600">
                        API Configuration
                      </span>
                    </button>
                  </div>
                </div>
              )}
              {showApiForm && (
                <form
                  onSubmit={this.handleFormSubmit}
                  className="mt-4 space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      API Host
                      <input
                        type="text"
                        value={apiHost}
                        onChange={e =>
                          this.setState({
                            apiHost: e.target.value,
                            app_details: false
                          })
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-4 py-2 rounded-md"
                  >
                    Change API Host
                  </button>
                  {!user && floOauth && (
                    <div className="mt-4">
                      <button
                        onClick={this._startOauthLogin}
                        className="text-blue-600 hover:pointer"
                      >
                        Support Login
                      </button>
                    </div>
                  )}
                  {user && (
                    <div className="mt-4">
                      <button
                        onClick={this.logout}
                        className="text-blue-600 hover:pointer"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </form>
              )}
            </div>
            {this.state.tests.map((test, index) => (
              <TestCard key={index} test={test} />
            ))}
          </div>
        </main>
      </div>
    );
  }
}

export default App;
